// Inter for reading, JetBrains Mono for labels and code: one look on iPhone, Android and web, where each
// used to fall back to its own system font (San Francisco, Roboto, Segoe UI).
//
// Phones: one file per weight, loaded at start-up (expo-font). React Native cannot pick a weight inside a
//         family that was loaded at run time, so `fontFor` turns the weight into the name of a file.
// Web:    one small variable font per family in public/fonts, declared once with @font-face. The browser
//         picks the weight, so styles keep their fontWeight.
//
// Screens do not know any of this: they keep writing `fontWeight: '700'` and `fontFamily: theme.mono`, and
// the Text in src/ui/Text.tsx asks `fontFor`. If the fonts cannot be loaded, the app runs on the system
// font exactly as before.
import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import type { TextStyle } from 'react-native';
import * as Font from 'expo-font';
import { FONT_FILES } from './fontFiles';

export type Kind = 'sans' | 'mono';

const WEB = Platform.OS === 'web';
const SANS_STACK = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
const MONO_STACK = '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
/** What the app's styles call "monospace" today. */
const MONO_NAMES = new Set(['monospace', 'Menlo', 'Courier', 'Courier New']);
const SANS_FILES: [number, string][] = [[450, 'Inter_400Regular'], [550, 'Inter_500Medium'], [650, 'Inter_600SemiBold'], [750, 'Inter_700Bold'], [Infinity, 'Inter_800ExtraBold']];
const MONO_FILES: [number, string][] = [[550, 'JetBrainsMono_400Regular'], [Infinity, 'JetBrainsMono_700Bold']];

let ready = WEB; // on web the browser swaps the font in when it arrives

/** Which of our two families a style asks for; 'other' when it names a font of its own. */
export function kindOf(family: string | undefined): Kind | 'other' | undefined {
  if (!family) return undefined;
  if (MONO_NAMES.has(family)) return 'mono';
  return 'other';
}

const weightOf = (w: TextStyle['fontWeight']): number => {
  if (w === 'bold') return 700;
  const n = typeof w === 'number' ? w : parseInt(String(w ?? ''), 10);
  return Number.isFinite(n) ? n : 400;
};
const fileFor = (kind: Kind, weight: number) => (kind === 'mono' ? MONO_FILES : SANS_FILES).find(([upTo]) => weight < upTo)![1];

/** The font to add to a text style, or undefined to leave it alone.
 *  `parent` is the family of the text this one sits inside, if any: nested text inherits from it. */
export function fontFor(style: TextStyle, parent: Kind | null): TextStyle | undefined {
  if (!ready) return undefined;
  const asked = kindOf(style.fontFamily);
  if (asked === 'other') return undefined;                                  // someone chose a font on purpose
  if (parent && !asked && style.fontWeight == null) return undefined;       // nested and unstyled: inherit
  const kind: Kind = asked ?? parent ?? 'sans';
  if (WEB) return { fontFamily: kind === 'mono' ? MONO_STACK : SANS_STACK };
  if (style.fontStyle === 'italic') return undefined;                       // no italic files are bundled: the system font slants
  return { fontFamily: fileFor(kind, weightOf(style.fontWeight)), fontWeight: 'normal' };
}

/** The navigation header's title is drawn by the navigator, not by our Text. */
export function headerTitleFont(): Pick<TextStyle, 'fontFamily' | 'fontWeight'> {
  if (!ready) return { fontWeight: '700' };
  return WEB ? { fontFamily: SANS_STACK, fontWeight: '700' } : { fontFamily: 'Inter_700Bold' };
}

const LATIN = 'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD';
const LATIN_EXT = 'U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF';

function declareWebFonts(): void {
  if (typeof document === 'undefined' || document.getElementById('helm-fonts')) return;
  const face = (family: string, file: string, weights: string, range: string) =>
    `@font-face { font-family: '${family}'; font-style: normal; font-weight: ${weights}; font-display: swap; src: url(/fonts/${file}.woff2) format('woff2'); unicode-range: ${range}; }`;
  const el = document.createElement('style');
  el.id = 'helm-fonts';
  el.textContent = [
    face('Inter', 'inter-latin-ext-wght-normal', '100 900', LATIN_EXT),
    face('Inter', 'inter-latin-wght-normal', '100 900', LATIN),
    face('JetBrains Mono', 'jetbrains-mono-latin-ext-wght-normal', '100 800', LATIN_EXT),
    face('JetBrains Mono', 'jetbrains-mono-latin-wght-normal', '100 800', LATIN),
    `html, body { font-family: ${SANS_STACK}; }`,
  ].join('\n');
  document.head.appendChild(el);
}

/** Load the fonts once at start-up. True when the app may draw: the fonts are in, or they could not be
 *  loaded (or took more than three seconds) and the app goes on with the system font. */
export function useAppFonts(): boolean {
  const [done, setDone] = useState(WEB);
  useEffect(() => {
    if (WEB) { declareWebFonts(); return; }
    let alive = true;
    const giveUp = setTimeout(() => { if (alive) setDone(true); }, 3000);
    Font.loadAsync(FONT_FILES)
      .then(() => { ready = true; })
      .catch(() => { ready = false; })
      .finally(() => { clearTimeout(giveUp); if (alive) setDone(true); });
    return () => { alive = false; clearTimeout(giveUp); };
  }, []);
  return done;
}
